import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import Lottie from "lottie-react";
import checkAnimationData from "../animations/checkAnimationData";
import useKakaoLoader from "../hooks/useKakaoLoader"; // ★ 1. Hook 불러오기

const AID_API_BASE_URL = "https://api.aid-100.com";

const incrementShareCount = async () => {
  const response = await fetch(`${AID_API_BASE_URL}/share/increment`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("공유 카운트 증가에 실패했습니다.");
  }

  return response.json();
};

const CompletePage = () => {
  const navigate = useNavigate();

  // ★ 2. Hook 실행 (이 한 줄이 index.html 스크립트 태그 역할을 대신합니다)
  const isKakaoLoaded = useKakaoLoader();

  // 페이지 진입 시 모든 쿠키 삭제
  useEffect(() => {
    // 모든 쿠키 가져오기
    const allCookies = Cookies.get();

    // 각 쿠키를 삭제 (경로와 도메인을 명시적으로 지정)
    Object.keys(allCookies).forEach((cookieName) => {
      // 루트 경로에서 삭제
      Cookies.remove(cookieName, { path: "/" });
      // 현재 경로에서도 삭제
      Cookies.remove(cookieName);
    });
  }, []);

  // 카카오톡 공유하기 함수
  const handleKakaoShare = async () => {
    // SDK가 아직 로드되지 않았으면 대기
    if (!isKakaoLoaded) {
      alert(
        "카카오톡 공유 기능을 불러오는 중입니다. 잠시 후 다시 시도해주세요.",
      );
      return;
    }

    // SDK가 로드되었지만 초기화되지 않았다면 초기화
    if (window.Kakao && !window.Kakao.isInitialized()) {
      window.Kakao.init("1c6c3d0206749465e0bcfcf58b020237");
    }

    // SDK가 없으면 에러
    if (!window.Kakao) {
      console.error("카카오 SDK가 로드되지 않았습니다.");
      alert("카카오톡 공유 기능을 사용할 수 없습니다.!");
      return;
    }

    const TEMPLATE_ID = 132584;

    // templateArgs를 명시적으로 정의 (템플릿에 정의된 변수명에 맞춰야 함)
    const templateArgs = {
      title: "듀오 집단소송 포털",
      description: "듀오 집단소송 접수를 온라인으로 간편하게 접수하세요",
    };

    try {
      // sendCustom 호출
      if (window.Kakao.Share && window.Kakao.Share.sendCustom) {
        window.Kakao.Share.sendCustom({
          templateId: TEMPLATE_ID,
          templateArgs: templateArgs,
        });

        // 공유 요청 완료 후 share/increment API 호출
        try {
          await incrementShareCount();
        } catch (shareError) {
          console.error("공유 카운트 처리 중 오류:", shareError);
          // 공유 카운트 실패 시 공유 진행은 막지 않음
        }
      } else {
        console.error("Kakao.Share.sendCustom이 사용할 수 없습니다.");
        alert("카카오톡 공유 기능을 사용할 수 없습니다.");
      }
    } catch (error) {
      console.error("공유 실패:", error);
      console.error("에러 상세:", {
        message: error.message,
        stack: error.stack,
        Kakao: window.Kakao,
      });
      alert("공유하기에 실패했습니다. 다시 시도해주세요.");
    }
  };
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <IconContainer>
            <Lottie
              animationData={checkAnimationData}
              loop={false}
              autoplay={true}
              style={{ width: 250, height: 250 }}
            />
          </IconContainer>

          <TitleContainer>
            <TitleLine>듀오 집단소송 접수가</TitleLine>
            <TitleLine>완료되었습니다</TitleLine>
          </TitleContainer>

          <CompleteButtonContainer>
            <KakaoShareButton onClick={handleKakaoShare}>
              <KakaoIcon src="/png/kakao_icon.png" alt="카카오톡" />
              카카오톡으로 공유하기
            </KakaoShareButton>
            <CompleteButton onClick={() => navigate("/select")}>
              완료하기
            </CompleteButton>
          </CompleteButtonContainer>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #f7f7f7;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;

  @media (min-width: 769px) {
    padding: 20px;
    background-color: #f7f7f7;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f7f7f7;

  @media (max-width: 768px) {
    min-height: 100vh;
    box-shadow: none;
    padding: 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  @media (min-width: 769px) {
    background-color: #f7f7f7;
    border-radius: 20px;
    min-height: auto;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;
  padding: 40px 20px;
  min-height: 100vh;

  @media (min-width: 769px) {
    min-height: auto;
  }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 250px;
  height: 250px;
  border-radius: 50%;
  margin-bottom: 40px;
  margin-top: auto;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: auto;
  text-align: center;
`;

const TitleLine = styled.h1`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.4;
  color: #333;
  margin: 0;
  text-align: center;
`;

const CompleteButtonContainer = styled.div`
  width: 100%;
  margin-top: auto;
  padding-bottom: 80px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (min-width: 769px) {
    max-width: 470px;
    margin-top: 40px;
  }
`;

const KakaoShareButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  background-color: #fee500;
  color: #000000;
  width: 100%;
  height: 56px;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s;

  &:hover {
    background-color: #fdd835;
  }
`;

const KakaoIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const CompleteButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #e31c20;
  color: white;
  width: 100%;
  height: 56px;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s;

  &:hover {
    background-color: #b01418;
  }
`;

export default CompletePage;
